/**
 * TT-03 — the totals bar's arithmetic and the CSV export, kept pure.
 *
 * This is the number a human compares against a subcontractor's invoice, so it
 * is unit-tested rather than eyeballed on screen. Two rules it encodes:
 *
 *   - **"not invoiceable" is not "0.00 €".** A type the project has switched
 *     off produces NO amount at all; showing a zero would read as "we owe
 *     nothing", when the truth is "this is not billed to the customer".
 *   - **A missing price is not a zero price either.** `weatherInvoiceable:
 *     true` with `weatherPricePerHour: null` means somebody has not set the
 *     rate yet — the hours are shown, the amount is withheld.
 */
import type { TimeTracking, TimeTrackingConfig } from "../types.js";

export interface TypeTotal {
  activityType: "standby_weather" | "standby_technical";
  label: string;
  minutes: number;
  hours: number;
  /** `null` when the type is not invoiceable OR no rate is set — see header. */
  amount: number | null;
  /** Why there is no amount, for the label the bar renders. */
  reason: "invoiceable" | "not_invoiceable" | "no_price";
}

const LABELS = {
  standby_weather: "Weather",
  standby_technical: "Technical",
} as const;

/**
 * Per-type totals for a set of rows, priced against the project config.
 *
 * `rows` is whatever the caller wants totalled — the whole filtered set or
 * just the current selection. Minutes come from `durationMinutes` (the wire
 * unit) and are converted once, here.
 */
export function typeTotals(
  rows: readonly Pick<TimeTracking, "activityType" | "durationMinutes">[],
  config: TimeTrackingConfig | undefined,
): TypeTotal[] {
  const out: TypeTotal[] = [];
  for (const activityType of [
    "standby_weather",
    "standby_technical",
  ] as const) {
    const minutes = rows
      .filter((r) => r.activityType === activityType)
      .reduce((n, r) => n + (r.durationMinutes ?? 0), 0);
    const hours = minutes / 60;
    const invoiceable =
      activityType === "standby_weather"
        ? (config?.weatherInvoiceable ?? false)
        : (config?.technicalInvoiceable ?? false);
    const price =
      activityType === "standby_weather"
        ? (config?.weatherPricePerHour ?? null)
        : (config?.technicalPricePerHour ?? null);

    let amount: number | null = null;
    let reason: TypeTotal["reason"] = "not_invoiceable";
    if (invoiceable && price != null) {
      // Round to cents at the END, once — rounding per row would drift.
      amount = Math.round(hours * price * 100) / 100;
      reason = "invoiceable";
    } else if (invoiceable) {
      reason = "no_price";
    }

    out.push({
      activityType,
      label: LABELS[activityType],
      minutes,
      hours,
      amount,
      reason,
    });
  }
  return out;
}

/** Total minutes across the given rows, regardless of type. */
export function totalMinutes(
  rows: readonly Pick<TimeTracking, "durationMinutes">[],
): number {
  return rows.reduce((n, r) => n + (r.durationMinutes ?? 0), 0);
}

// ─── CSV ─────────────────────────────────────────────────────────────────────

/**
 * RFC-4180 field escaping. A description going onto an invoice routinely
 * contains commas, quotes and newlines — an unescaped one silently shifts every
 * later column, which is exactly the kind of quiet corruption a back-office
 * export must not have.
 */
export function csvField(value: string | number | null | undefined): string {
  if (value == null) return "";
  const s = typeof value === "number" ? String(value) : value;
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export const CSV_COLUMNS = [
  "pilot",
  "type",
  "appliesTo",
  "project",
  "start",
  "end",
  "durationHours",
  "description",
  "status",
] as const;

/**
 * Build the CSV for the filtered set. Times are ISO (unambiguous in a
 * spreadsheet import), duration is decimal hours (what an invoice line is
 * written in), status is the word the table shows.
 */
export function buildCsv(rows: readonly TimeTracking[]): string {
  const lines = [CSV_COLUMNS.join(",")];
  for (const r of rows) {
    lines.push(
      [
        csvField(r.userLabel ?? r.userId),
        csvField(r.activityType),
        csvField(r.anchorLabel),
        csvField(r.projectLabel),
        csvField(r.startTime),
        csvField(r.endTime),
        csvField(
          r.durationMinutes == null ? "" : (r.durationMinutes / 60).toFixed(2),
        ),
        csvField(r.description),
        csvField(r.isLocked ? "invoiced" : "editable"),
      ].join(","),
    );
  }
  // Trailing newline: some spreadsheet importers drop the last row without it.
  return `${lines.join("\r\n")}\r\n`;
}

/** `time-trackings-<project>-2026-08-16.csv`, safe on every filesystem. */
export function csvFilename(
  projectLabel: string | null,
  isoDate: string,
): string {
  const slug = (projectLabel ?? "project")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  return `time-trackings-${slug || "project"}-${isoDate.slice(0, 10)}.csv`;
}
